<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import OnboardingAPI from 'dashboard/api/onboarding';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute, isCloudFeatureEnabled } = useAccount();
const { isAdmin } = useAdmin();

const onboardingStatus = ref(null);
const isDismissed = ref(false);
const isStarting = ref(false);

const isOnboardingWizardEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.ONBOARDING_WIZARD)
);

// Shown once, over whatever screen the admin lands on, the very first time they visit
// an account (status still not_started). Hidden once they're already on the wizard's
// own route, since Step00Welcome.vue renders the same copy there as a full page.
const shouldShow = computed(
  () =>
    !isDismissed.value &&
    isOnboardingWizardEnabled.value &&
    isAdmin.value &&
    onboardingStatus.value === 'not_started' &&
    route.name !== 'account_onboarding'
);

const dismiss = () => {
  isDismissed.value = true;
};

const startConfiguration = async () => {
  isStarting.value = true;
  try {
    await OnboardingAPI.update({ status: 'in_progress', current_step: 1 });
    router.push(accountScopedRoute('account_onboarding'));
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  } finally {
    isStarting.value = false;
  }
};

onMounted(() => {
  if (!isOnboardingWizardEnabled.value || !isAdmin.value) return;

  OnboardingAPI.get()
    .then(({ data }) => {
      onboardingStatus.value = data.status;
    })
    .catch(() => {
      // Best-effort — failing silently here just keeps the modal hidden.
    });
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Modal
    v-if="shouldShow"
    :show="shouldShow"
    :on-close="dismiss"
    :close-on-backdrop-click="false"
    show-close-button
  >
    <div class="flex flex-col items-center gap-4 p-8 text-center">
      <h1 class="text-2xl font-semibold text-n-slate-12">
        {{ t('ONBOARDING.WELCOME.TITLE') }}
      </h1>
      <p class="text-n-slate-11">
        {{ t('ONBOARDING.WELCOME.DESCRIPTION') }}
      </p>
      <p class="text-n-slate-11">
        {{ t('ONBOARDING.WELCOME.QUESTION') }}
      </p>
      <Button
        type="button"
        trailing-icon
        icon="i-lucide-arrow-right"
        :label="t('ONBOARDING.WELCOME.CTA')"
        :is-loading="isStarting"
        class="mt-2"
        @click="startConfiguration"
      />
    </div>
  </Modal>
</template>
